/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useTranslation } from '@scuf/localization';
import descriptors from '../../../../locales/en-US/descriptors';
import { isObjectOrArrayAndEmpty } from '../../../shared/Utils/Utils';
import useConnectorAndDynamicProps from '../../../shared/useConnectorAndDynamicProps/useConnectorAndDynamicProps';
import ConnectionError from '../../../shared/ErrorComponents/ConnectionError';
import withLoadingStatus from '../../../shared/StatusWrappers/LoadingStatus';
import withErrorStatus from '../../../shared/StatusWrappers/ErrorStatus';
import useMounted from '../../../shared/useMount/useMount';
import EmaasEnergyUsageComponent from '../EmaasEnergyUsageComponent/EmaasEnergyUsageComponent';
import {
  IAny,
  IEmaasEnergyUsageComponentDataArea,
  IEmaasEnergyUsageComponentDataAreaV2,
  IEmaasEnergyUsageHOCProps,
  ISiteCard
} from '../../IEnergyManagementProps';
import FilterScope1Action from '../../../../context/FilterScope1Context/FilterScope1Actions';
import { useInterval } from '../../../shared/useInterval/useInterval';
import {
  POLLING_MILLI_SECONDS,
  POLLING_SECONDS
} from '../../../shared/Utils/Constants';
import EmaasBasicSubscriptionEnergyUsageComponentSite from '../EmaasBasicSubscriptionEnergyUsageComponent/EmaasBasicSubscriptionEnergyUsageComponentSite';
import {
  EnergyUsageDataSourceType,
  EnergyUsageField
} from '../../../shared/Enums';
import {
  getEnergyEndDateBasedOnSub,
  getEnergyStartDate
} from '../../../shared/Utils/EnergyUsagesViewDetailsUtils';
import { PollingContext } from '../../../../context/PollingContext/PollingContext';
import { getIntervalType } from '../../../shared/Utils/PortfolioEnergy/PortfolioEnergyVDUtils';
import { useFilterScope1Context } from '../../../../context/FilterScope1Context/FilterScope1Context';
import EmaasNILMEnergyUsageComponent from '../EmaasNILMEnergyUsageComponent/EmaasNILMEnergyUsageComponent';
import { Card, Icon, Tab, Tooltip, Button, Select } from '@scuf/common';
import NoData from '../../../shared/ErrorComponents/NoData';
import moment from 'moment';

import EnergyIconActive from '../../../../images/EnergyIconActive.svg';
import EnergyIconInActive from '../../../../images/EnergyIconInActive.svg';
import WaterIconActive from '../../../../images/WaterIconActive.svg';
import WaterIconInActive from '../../../../images/WaterIconInActive.svg';
import { ApiContext } from '../../../../context/ApiContext';
import {
  IIconTabbedSwitchProps,
  IconTabbedSwitch
} from '../../../shared/TabbedSwitch/IconTabbedSwitch/IconTabbedSwitch';
interface IEmaasEnergyUsageHOCContent extends ISiteCard {
  data:
    | IEmaasEnergyUsageComponentDataArea
    | IEmaasEnergyUsageComponentDataAreaV2;
  emaasContext?: IAny;
}
interface activeIndexProps {
  activeIndex: number;
}
const EmaasEnergyUsageHOCContent = <T extends activeIndexProps>(
  props: IEmaasEnergyUsageHOCContent & T
) => {
  const d = descriptors.energyManagement;
  const [t] = useTranslation();
  const { data, onRefresh, emaasContext, activeIndex } = props;
  if (isObjectOrArrayAndEmpty(data)) {
    return (
      <div className="errorDisplay common-error-container">
        <ConnectionError
          title={t(d.CONNECTIONERRORTITLE)}
          message={t(d.CONNECTIONERROR)}
          onRefresh={onRefresh}
        />
      </div>
    );
  }

  return (
    <>
      {data.subscriptionType === EnergyUsageField.BASICSUBS ? (
        activeIndex === 1 ? (
          <EmaasNILMEnergyUsageComponent
            data={data as IEmaasEnergyUsageComponentDataAreaV2}
            viewType={EnergyUsageField.NILM}
            emaasContext={emaasContext}
          />
        ) : (
          <EmaasBasicSubscriptionEnergyUsageComponentSite
            data={data}
            viewType={EnergyUsageField.SITEENERGY}
          />
        )
      ) : (
        <EmaasEnergyUsageComponent data={data} />
      )}
    </>
  );
};

const EmaasEnergyUsageHOCWithLoader = withLoadingStatus(
  withErrorStatus(EmaasEnergyUsageHOCContent)
);

// Refresh every hour
const ENERGYUSAGE_REFRESH_INTERVAL = POLLING_SECONDS * POLLING_MILLI_SECONDS;
const EmaasEnergyUsageHOCSite = (props: IEmaasEnergyUsageHOCProps) => {
  const {
    bearerToken,
    requestContext,
    emaasContext,
    dataSourceType,
    setDataSourceType
  } = props;

  const [isOutOfRange, setIsOutOfRange] = useState<boolean>(false);
  const [activeIndex, setIndexTab] = useState(0);
  const startDate = getEnergyStartDate(
    requestContext.context.startTime!,
    requestContext.context.subscriptionType!,
    emaasContext,
    requestContext.context.subscriptionType === 'BASIC_SUBSCRIPTION' &&
      emaasContext.isNILMSubscribed &&
      emaasContext.toggleFeatureNILM2 &&
      activeIndex === 0,
    dataSourceType === EnergyUsageDataSourceType.UTILITY_METER
  );
  const endDate = getEnergyEndDateBasedOnSub(
    requestContext.context.subscriptionType!,
    requestContext.context.endTime!,
    requestContext.context.startTime!,
    emaasContext,
    requestContext.context.subscriptionType === 'BASIC_SUBSCRIPTION' &&
      emaasContext.isNILMSubscribed &&
      emaasContext.toggleFeatureNILM2 &&
      activeIndex === 0
  );

  const isDateRangeValid = () => {
    const sDate = moment(
      getEnergyStartDate(
        requestContext.context.startTime!,
        requestContext.context.subscriptionType!
      ),
      'YYYY-MM-DD'
    );
    const eDate = moment(
      getEnergyEndDateBasedOnSub(
        requestContext.context.subscriptionType!,
        requestContext.context.endTime!,
        requestContext.context.startTime!
      ),
      'YYYY-MM-DD'
    );
    console.log(
      " eDate.diff(sDate, 'days') <= 365",
      moment(eDate).diff(sDate, 'days')
    );
    return eDate.diff(sDate, 'days') <= 365;
  };

  const changeTabIndex = (index: number) => {
    if (index != activeIndex) {
      setIndexTab(index);
      setIsLoading(true);
    }
  };
  const getConnectorConfigName = () => {
    if (
      requestContext.context.subscriptionType === 'BASIC_SUBSCRIPTION' &&
      emaasContext.isNILMSubscribed &&
      emaasContext.toggleFeatureNILM2 &&
      activeIndex === 1
    ) {
      return 'emaas-energy-Disaggregation';
    } else if (
      requestContext.context.subscriptionType === 'BASIC_SUBSCRIPTION' &&
      activeIndex === 0
    ) {
      return 'emaas-energy-usage';
    } else {
      return 'emaas-energy-usage';
    }
  };
  /*
  const connectorConfig = {
    name: getConnectorConfigName(),
    options: {
      startDate: startDate,
      endDate: endDate,
      allDetails: 'true',
      intervalType: getIntervalType(startDate, endDate)
    }
  };
  */

  const [t] = useTranslation();
  const d = descriptors.energyManagement;

  // Water and Energy Tab
  const availableTabs: IIconTabbedSwitchProps['tabList'] = [
    {
      id: 'All',
      label: t(d.ENERGYTAB),
      iconSVG: EnergyIconActive,
      iconInactiveSVG: EnergyIconInActive
    },
    {
      id: 'Water',
      label: t(d.WATERTAB),
      iconSVG: WaterIconActive,
      iconInactiveSVG: WaterIconInActive
    }
  ];
  const [currentTab, setCurrentTab] = useState<string>('All');

  const connectorConfig: IAny = useMemo(() => {
    if (currentTab === 'All') {
      return {
        name: getConnectorConfigName(),
        options: {
          startDate: startDate,
          endDate: endDate,
          allDetails: 'true',
          intervalType: getIntervalType(startDate, endDate),
          dataSource: dataSourceType?.valueOf() || ''
        }
      };
    }
    return {
      name: getConnectorConfigName(),
      options: {
        startDate: startDate,
        endDate: endDate,
        allDetails: 'true',
        intervalType: getIntervalType(startDate, endDate),
        utilityType: currentTab // Use state variable
      }
    };
  }, [startDate, endDate, currentTab, dataSourceType]);

  const [fetchedData] = useConnectorAndDynamicProps({
    connectorConfig: connectorConfig,
    requestContext,
    bearerToken
  });

  const [silentLoading, setIsSilentLoading] = useState<boolean>(false);
  const [pollingCount, setPollingCount] = useState(0);

  const isMounted = useMounted();

  /*
  const [dataConnector] = useConnectorAndDynamicProps({
    connectorConfig,
    requestContext,
    bearerToken
  });
  */

  const onRefresh = () => fetchedData?.actions?.getAll();

  useEffect(() => {
    setIsSilentLoading(false);
    if (isMounted) {
      if (isDateRangeValid()) {
        setIsOutOfRange(false);
        onRefresh();
      } else {
        setIsOutOfRange(true);
      }
    }
  }, [requestContext, activeIndex]);

  useEffect(() => {
    if (isMounted) {
      onRefresh();
    }
  }, [pollingCount]);

  useEffect(() => {
    if (!isDateRangeValid()) {
      setIsOutOfRange(true);
    }
  }, []);

  const { all, loading, error, status } = fetchedData.data;

  const EmaasEnergyUsage = (all as unknown) as IEmaasEnergyUsageComponentDataArea;

  // Polling mechanism 15 min
  useInterval(() => {
    if (EmaasEnergyUsage?.subscriptionType === EnergyUsageField.PREMIUMSUBS) {
      setIsSilentLoading(true);
      setPollingCount(pollingCount + 1);
    }
  }, ENERGYUSAGE_REFRESH_INTERVAL);

  const removeSilentLoadingonRefresh = () => {
    setIsSilentLoading(false);
    return onRefresh();
  };

  const isDataAvailable = EmaasEnergyUsage ? true : false;
  const [isLoading, setIsLoading] = useState<boolean>(loading);
  const [statusCode, setStatusCode] = useState<number | null>(status);
  const [errorInfo, setErrorInfo] = useState(error);
  const [data, setData] = useState({} as any);
  const [state, dispatch] = useFilterScope1Context();
  const pollingContext = useContext(PollingContext);
  const { setPollingTime } = pollingContext;

  useEffect(() => {
    setIsLoading(loading);
    if ((isDataAvailable || error || status) && !loading) {
      if (isDataAvailable) {
        //setData(EmaasEnergyUsage);
        setData((prevData: IEmaasEnergyUsageComponentDataArea) => ({
          ...prevData,
          ...EmaasEnergyUsage
        }));
        setPollingTime && setPollingTime();
        dispatch(
          FilterScope1Action.subscriptionValue(
            EmaasEnergyUsage.subscriptionType as IAny
          )
        );
      }
      setErrorInfo(error);
    }
    setStatusCode(status);
  }, [isDataAvailable, loading, status]);

  const apiContext = useContext(ApiContext);
  const {
    enableWaterUsageDashboards,
    enableConsumptionSourceSelect,
    cemsIntervalEnergySourceOption
  } = apiContext;

  useEffect(() => {
    if (isMounted) {
      onRefresh();
    }
  }, [currentTab, dataSourceType]);

  useEffect(() => {
    if (enableConsumptionSourceSelect) {
      if (currentTab === 'water') {
        setDataSourceType &&
          setDataSourceType(EnergyUsageDataSourceType.LIVE_METER);
      }
    }
  }, [enableConsumptionSourceSelect, currentTab]);

  const kpiContainer =
    apiContext.showEnergyStarWidget || apiContext.isEOSubscribed
      ? 'kpi-content-container with-energy-star'
      : 'kpi-content-container';

  return (
    <div
      id="cems-site-energy-usage-card"
      className="emaas-energyusage-container kpi-card-common cems-site-energy-usage-card"
    >
      <Card>
        {requestContext.context.subscriptionType === 'BASIC_SUBSCRIPTION' &&
        emaasContext.isNILMSubscribed &&
        emaasContext.toggleFeatureNILM2 ? (
          <Card.Header className="kpi-header-container basicNilmHeader">
            <Tab
              className="tabForSiteDashboard"
              defaultActiveIndex={activeIndex}
              onTabChange={(activeIndex) => changeTabIndex(activeIndex)}
            >
              <Tab.Pane title={t(d.ENERGYUSAGE)} key={0}></Tab.Pane>
              <Tab.Pane title={t(d.ENERGYDISAGGREGATION)} key={1}></Tab.Pane>
            </Tab>
            <Tooltip
              element={
                <Icon root="common" name="badge-info" size="small" key={1} />
              }
              event="hover"
              header={
                activeIndex === 1 ? t(d.NILMTOOLTIPHEADER) : t(d.ENERGYUSAGE)
              }
              content={
                activeIndex === 1
                  ? t(d.EMAASNILMTOOLTIP)
                  : requestContext.context.subscriptionType ===
                    'BASIC_SUBSCRIPTION'
                  ? t(d.EMAASENERGYUSAGETOOLTIPPREV)
                  : t(d.EMAASENERGYUSAGETOOLTIP)
              }
              position="right center"
              size="small"
            />
          </Card.Header>
        ) : (
          <Card.Header className="kpi-header-container consumption-header-with-tabs">
            <div className="kpi-card-title-wrapper">
              <div style={{ marginRight: '5px' }} className="kpi-card-title">
                {enableWaterUsageDashboards
                  ? t(d.ENERGYUSAGECONSUMPTION)
                  : t(d.ENERGYUSAGE)}
              </div>
              <Tooltip
                element={
                  <Icon
                    style={{ marginBottom: '4px' }}
                    root="common"
                    name="badge-info"
                    size="small"
                    key={1}
                  />
                }
                event="hover"
                header={
                  enableWaterUsageDashboards
                    ? t(d.ENERGYUSAGECONSUMPTION)
                    : t(d.ENERGYUSAGE)
                }
                content={
                  requestContext.context.subscriptionType ===
                  'BASIC_SUBSCRIPTION'
                    ? t(d.EMAASENERGYUSAGETOOLTIPPREV)
                    : enableConsumptionSourceSelect
                    ? dataSourceType === EnergyUsageDataSourceType.UTILITY_METER
                      ? t(d.CEMSPORTFOLIO_DATA_SOURCE_UTIL_TOOLTIP)
                      : dataSourceType ===
                        EnergyUsageDataSourceType.UTILITY_INTERVAL
                      ? t(d.CEMSPORTFOLIO_DATA_SOURCE_INTERVAL_TOOLTIP)
                      : enableWaterUsageDashboards && currentTab === 'water'
                      ? t(d.CEMSPORTFOLIO_DATA_SOURCE_LIVE_WATER_TOOLTIP)
                      : t(d.CEMSPORTFOLIO_DATA_SOURCE_LIVE_TOOLTIP)
                    : enableWaterUsageDashboards
                    ? t(d.EMAASCONSUMPTIONTOOLTIP)
                    : t(d.EMAASENERGYUSAGETOOLTIP)
                }
                position="right center"
                size="small"
              />
            </div>
            <div>
              {enableWaterUsageDashboards &&
                (enableConsumptionSourceSelect ? (
                  <Select
                    className="kpi-card-select cems-energy-water-select"
                    options={[
                      {
                        value: 'All',
                        text: t(d.ENERGYNOUNIT)
                      },
                      {
                        value: 'water',
                        text: t(d.WATER)
                      }
                    ]}
                    value={currentTab}
                    onChange={(value) => setCurrentTab(value as string)}
                  />
                ) : (
                  <IconTabbedSwitch
                    activeTab={currentTab}
                    hideInactiveText
                    tabList={availableTabs}
                    onTabChange={(id) => {
                      setCurrentTab(id);
                    }}
                  />
                ))}
              {enableConsumptionSourceSelect && (
                <Select
                  className="kpi-card-select cems-energy-datasource-select"
                  options={[
                    {
                      value: EnergyUsageDataSourceType.UTILITY_METER,
                      text: t(d.UTILITYPROVIDERNEW),
                      disabled: currentTab === 'water'
                    },
                    ...(cemsIntervalEnergySourceOption
                      ? [
                          {
                            value: EnergyUsageDataSourceType.UTILITY_INTERVAL,
                            text: t(d.UTILITYINTERVAL),
                            disabled: currentTab === 'water'
                          }
                        ]
                      : []),
                    {
                      value: EnergyUsageDataSourceType.LIVE_METER,
                      text: t(d.LIVEREADING)
                    }
                  ]}
                  value={dataSourceType}
                  defaultValue={EnergyUsageDataSourceType.UTILITY_METER}
                  onChange={(value) =>
                    setDataSourceType &&
                    setDataSourceType(value as EnergyUsageDataSourceType)
                  }
                />
              )}
            </div>
          </Card.Header>
        )}

        <Card.Content className={kpiContainer}>
          {isOutOfRange ? (
            <div className="noDataDisplay common-error-container">
              <NoData message={t(d.UTILITYYEAROUTOFRANGE)} />
            </div>
          ) : (
            <EmaasEnergyUsageHOCWithLoader
              loading={isLoading}
              error={errorInfo}
              statusCode={statusCode}
              onRefresh={removeSilentLoadingonRefresh}
              data={data}
              loadingText={t(d.LOADING)}
              title={t(d.CONNECTIONERRORTITLE)}
              noDataMessage={t(d.NODATAAVAILABLE)}
              connectionErrorMessage={t(d.CONNECTIONERROR)}
              silentLoading={silentLoading}
              emaasContext={emaasContext}
              activeIndex={activeIndex}
            />
          )}
        </Card.Content>
      </Card>
    </div>
  );
};
export default EmaasEnergyUsageHOCSite;
